"use client";

import { c as _c } from "react/compiler-runtime";
import { memo } from "react";
import { Check, Clapperboard, ImageIcon, X } from "lucide-react";
import type { UploadTilePreview } from "@/lib/upload-tile-preview";
export type UploadTileStatus = "pending" | "uploading" | "saving" | "success" | "error";
interface UploadPrintTileProps {
  fileId: string;
  name: string;
  isVideo: boolean;
  status: UploadTileStatus;
  percentage: number;
  preview?: UploadTilePreview | null;
  /** Stagger for the landing animation; omitted tiles appear without it. */
  landDelayMs?: number;
}

// A cream veil over the photo lifts as bytes upload, so the print "develops".
// Only a server-confirmed save clears it completely.
function getVeilOpacity(status: UploadTileStatus, percentage: number): number {
  switch (status) {
    case "pending":
      return 0.9;
    case "uploading":
      return 0.9 - 0.75 * (Math.min(100, Math.max(0, percentage)) / 100);
    case "saving":
      return 0.15;
    case "success":
      return 0;
    case "error":
      return 0.55;
  }
}

// Stable per-file tilt between -2.4 and 2.4 degrees.
export function getPrintTilt(fileId: string): number {
  let hash = 0;
  for (let index = 0; index < fileId.length; index += 1) {
    hash = hash * 31 + fileId.charCodeAt(index) | 0;
  }
  return (Math.abs(hash) % 9 - 4) * 0.6;
}
function UploadPrintTile(t0) {
  const $ = _c(39);
  const {
    fileId,
    name,
    isVideo,
    status,
    percentage,
    preview,
    landDelayMs
  } = t0;
  let t1;
  if ($[0] !== fileId) {
    t1 = getPrintTilt(fileId);
    $[0] = fileId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const tilt = t1;
  const isSaved = status === "success";
  const isFailed = status === "error";
  const shouldLand = landDelayMs !== undefined;
  const PlaceholderIcon = isVideo ? Clapperboard : ImageIcon;
  const t2 = `relative w-[68px] shrink-0 rounded-[4px] bg-white p-1 pb-[11px] shadow-[0_8px_16px_-10px_rgba(56,40,22,0.55)] ring-1 ring-[#3b2a17]/[0.06] transition-[rotate] duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] motion-reduce:transition-none ${shouldLand ? "motion-safe:animate-upload-print-land" : ""}`;
  const t3 = `${tilt}deg`;
  const t4 = isSaved ? "0deg" : `${tilt}deg`;
  const t5 = shouldLand ? `${landDelayMs}ms` : undefined;
  let t6;
  if ($[2] !== t3 || $[3] !== t4 || $[4] !== t5) {
    t6 = {
      "--upload-tilt": t3,
      rotate: t4,
      animationDelay: t5
    };
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const t7 = t6 as React.CSSProperties;
  let t8;
  if ($[6] !== PlaceholderIcon) {
    t8 = <div className="absolute inset-0 flex items-center justify-center text-[#a5794c]/70"><PlaceholderIcon className="h-6 w-6" aria-hidden="true" /></div>;
    $[6] = PlaceholderIcon;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== name || $[9] !== preview) {
    t9 = preview ? <img src={preview.url} alt={name} className="absolute inset-0 h-full w-full object-cover [image-orientation:none]" style={{
      transform: preview.transform
    }} draggable={false} decoding="async" loading="lazy" /> : <span className="sr-only">{name}</span>;
    $[8] = name;
    $[9] = preview;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== percentage || $[12] !== status) {
    t10 = getVeilOpacity(status, percentage);
    $[11] = percentage;
    $[12] = status;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== t10) {
    t11 = <div className="absolute inset-0 bg-[#efe6d8] transition-opacity duration-500 ease-out" style={{
      opacity: t10
    }} />;
    $[14] = t10;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== status) {
    t12 = status === "saving" && <div className="absolute inset-0 overflow-hidden"><div className="absolute inset-y-0 left-0 w-2/3 bg-gradient-to-r from-transparent via-white/70 to-transparent motion-safe:animate-upload-shimmer motion-reduce:hidden" /></div>;
    $[16] = status;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== isSaved) {
    t13 = isSaved && <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-white text-emerald-600 shadow-[0_2px_6px_rgba(0,0,0,0.18)] motion-safe:animate-upload-pop"><Check className="h-3.5 w-3.5" strokeWidth={3} aria-hidden="true" /></span>;
    $[18] = isSaved;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== isFailed) {
    t14 = isFailed && <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-white text-red-600 shadow-[0_2px_6px_rgba(0,0,0,0.18)]"><X className="h-3.5 w-3.5" strokeWidth={3} aria-hidden="true" /></span>;
    $[20] = isFailed;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== t11 || $[23] !== t12 || $[24] !== t13 || $[25] !== t14 || $[26] !== t8 || $[27] !== t9) {
    t15 = <div className="relative aspect-square overflow-hidden rounded-[2px] bg-gradient-to-br from-[#f6e7d2] via-[#f3d9b8] to-[#e9c69c]">{t8}{t9}{t11}{t12}{t13}{t14}</div>;
    $[22] = t11;
    $[23] = t12;
    $[24] = t13;
    $[25] = t14;
    $[26] = t8;
    $[27] = t9;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== isFailed || $[30] !== isSaved || $[31] !== percentage || $[32] !== status) {
    t16 = !isSaved && !isFailed && <div className="absolute inset-x-1 bottom-1 h-[3px] overflow-hidden rounded-full bg-[#efe6d8]"><div className="h-full w-full origin-left rounded-full bg-gg-orange-500 transition-transform duration-300 ease-out" style={{
        transform: `scaleX(${status === "saving" ? 1 : Math.min(100, Math.max(0, percentage)) / 100})`
      }} /></div>;
    $[29] = isFailed;
    $[30] = isSaved;
    $[31] = percentage;
    $[32] = status;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t15 || $[35] !== t16 || $[36] !== t2 || $[37] !== t7) {
    t17 = <div className={t2} style={t7}>{t15}{t16}</div>;
    $[34] = t15;
    $[35] = t16;
    $[36] = t2;
    $[37] = t7;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  return t17;
}
export default memo(UploadPrintTile);